import { useEffect, useState } from 'react';

import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';

import { claimQueries } from '@repo/api/claim/claims';
import {
	useDocumentDelete,
	useDocumentSubmit,
} from '@repo/api/claim/documents';
import { useUser } from '@repo/auth';
import { DocumentsPage } from '@repo/features/documents';

import { ErrorMessage } from './-components/ErrorMessage';
import { Route as BusinessRoute } from './business';

export const Route = createFileRoute(
	'/_authenticated/claim/$claimId/_claim/documents',
)({
	beforeLoad: ({ params, context }) => {
		if (!context.user?.permissions.includes('claim.document.view')) {
			throw redirect({
				to: BusinessRoute.to,
				params,
			});
		}
	},
	component: function DocumentsTab() {
		const params = Route.useParams();

		const navigate = Route.useNavigate();

		const claimId = params.claimId;

		const [defaultComparisonDate] = useState(() => new Date().toISOString());

		const { permissions } = useUser();

		const { data: claim, isFetching } = useSuspenseQuery(
			claimQueries.id(claimId),
		);

		useEffect(() => {
			if (!isFetching && !claim.questionnaireCompleted) {
				void navigate({
					to: BusinessRoute.to,
				});
			}
		}, [claim.questionnaireCompleted, isFetching, navigate]);

		const { mutateAsync: deleteDocument } = useDocumentDelete(claim.claimId);

		const { mutateAsync: submitDocument, isPending: submitting } =
			useDocumentSubmit(claim.claimId);

		if (claim.stage == null) {
			return <ErrorMessage message="Claim not found" />;
		}

		return (
			<DocumentsPage
				title="Claim Documents"
				documents={claim.claimDocuments}
				stage={claim.stage}
				showFileProgress
				deleteDocument={deleteDocument}
				permissions={{
					download: permissions.includes('claim.document.download'),
					close: permissions.includes('claim.close'),
				}}
				canUpload={permissions.includes('claim.document.upload')}
				countryId={claim.claimInsured.countryId}
				comparisonDate={claim.dateofLoss || defaultComparisonDate}
				submitDocument={submitDocument}
				submitting={submitting}
				introAcknowledgement="completing the business questionnaire"
				uploadTarget="your claim"
			/>
		);
	},
	head: (ctx) => ctx.match.context.setTitle('Claim', 'Documents'),
});
